import React, {
  useEffect, useMemo, useState,
} from 'react';

import { Flash, Tooltip } from '@primer/react';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';

import { useQuery } from '@apollo/client';

import { FETCH_PIPELINES_ASSOC_MODELS } from '../../utils/model_server';

import { AssayConfig } from './AssayConfig';
import Button from '../common/Button/Button';
import CardHeader from '../common/Labels/CardHeader';
import CardView from '../common/CardView/CardView';
import CreateAssayStepOne from './CreateAssayStepOne';
import Label from '../common/Labels/Label';
import PreviewHistorical from './PreviewHistorical';
import ScrollableBox from '../common/overlays/ScrollableBox';
import { TimeRangeProvider } from '../contexts/TimeRangeContext/TimeRangeContext';

import {
  areEdgeValuesInappropriate,
  areEdgesRequiredAndIncomplete,
  areWeightsRequiredAndIncomplete,
} from '../../utils/assay_utils';
import createAssay from '../../utils/api_functions/assayCreation';
import { sortEdgeLocations } from '../edge/edge_utils';

import useClientContext from '../contexts/ClientData/ClientData';
import { useMatchMutate } from '../../utils/hooks/use_match_mutate';
import usePipelineData from '../pipelines/PipelineDataContext';
import { usePipelineEdgeData } from '../VersionHistoryTimeline/PublishPipelineContext';
import useWorkspaceContext from '../workspaces/WorkspaceContext/WorkspaceContext';

import { WALLAROO_OPS_CENTER_REGEX } from '../../constants/wallaroo.constants';

import './style.css';

const CREATE_ASSAY_SCREENS = {
  SELECT_MODEL: 1,
  SELECT_BASELINE: 2,
  PREVIEW_HISTORICAL: 3,
}

const PreviewAndFinishWrapper = ({
  newAssayConfig, setError, setNewAssayConfig, setBaselineSummary,
}) => (
  <TimeRangeProvider usePollInterval={false} defaultTimeRange={{
    fromDate: newAssayConfig.baseline?.calculated?.fixed_window?.start ? new Date(newAssayConfig.baseline.calculated.fixed_window.start) : startOfDay(subDays(new Date(), 1)),
    toDate: newAssayConfig.baseline?.calculated?.fixed_window?.end ? new Date(newAssayConfig.baseline.calculated.fixed_window.end) : startOfDay(new Date()),
    type: 'custom'
  }}>
    <ScrollableBox>
      <PreviewHistorical
        assayConfig={newAssayConfig}
        setAssayConfig={setNewAssayConfig}
        setBaselineSummary={setBaselineSummary}
        setError={setError}
      />
    </ScrollableBox>
  </TimeRangeProvider>
)

const ToolTipButtonWrapper = ({
  screen, handleBack, handleNext, nextIsDisabled, error, onBuild,
}) => {
  // Shows tooltip on this screen only
  if (screen === CREATE_ASSAY_SCREENS.SELECT_MODEL) {
    return (
      <Tooltip id="scrollTooltip" aria-label="Scroll down for more details" noDelay>
        <div className="w-full flex flex-row-reverse justify-between pt-4" style={{ boxShadow: '0px -11px 8px -10px #CCC' }}>
          <div className="flex justify-end">
            {
              screen !== CREATE_ASSAY_SCREENS.PREVIEW_HISTORICAL
                ? <Button
                  variant="primary"
                  size="large"
                  onClick={handleNext}
                  disabled={nextIsDisabled || error}>Next</Button>
                : <Button
                  className="ml-2"
                  variant="primary"
                  size="large"
                  // TODO: Fire the request up, close the modal  
                  onClick={onBuild}
                  disabled={nextIsDisabled || error}
                >
                  Finish & Create Assay
                </Button>
            }
          </div>
        </div>
      </Tooltip>
    )
  }

  return (
    <div className="w-full flex justify-between pt-4">
      <Button onClick={handleBack} size="large">Back</Button>
      <div className="flex justify-end">
        {
          screen !== CREATE_ASSAY_SCREENS.PREVIEW_HISTORICAL
            ? <Button
              variant="primary"
              size="large"
              onClick={handleNext}
              disabled={nextIsDisabled || error}>Next</Button>
            : <Button
              className="ml-2"
              variant="primary"
              size="large"
              // TODO: Fire the request up, close the modal  
              onClick={onBuild}
              disabled={nextIsDisabled || error}
            >
              Finish & Create Assay
            </Button>
        }
      </div>
    </div>
  )
}

export const AssayCreationModalWrapper = ({
  pipelineId,
  pipelineName,
  models,
  workspace,
  workspaceId,
  onClose,
  inferenceData,
  isEdgeEnabled = false,
}) => {
  const [screen, setScreen] = useState(CREATE_ASSAY_SCREENS.SELECT_MODEL);
  const [newAssayConfig, setNewAssayConfig] = useState(new AssayConfig({ pipeline_name: pipelineName, pipeline_id: pipelineId }));
  const [baselineSummary, setBaselineSummary] = useState(null);
  const [nextIsDisabled, setNextIsDisabled] = useState(false);
  const [error, setError] = useState(null);
  const matchMutate = useMatchMutate();
  const { isAssaysV2Enabled } = useClientContext();

  const isSelectModelScreen = screen === CREATE_ASSAY_SCREENS.SELECT_MODEL;

  const {
    listOfEdgesForPipelines,
    selectedEdgeLocations: selectedEdges,
  } = usePipelineEdgeData()
  const selectedEdgeNames = useMemo(() => selectedEdges?.length ? selectedEdges.map(({ name }) => name) : [], [selectedEdges]);

  const numberOfSelectedEdges = selectedEdges?.length || 0;
  const hasSelectedEdgeLocations = selectedEdges?.length || false;

  const workspaceName = workspace?.name;
  useEffect(() => {
    setNewAssayConfig(x => x.setPipeline(pipelineId, pipelineName).setWorkspace(workspaceId, workspaceName));
  }, [pipelineId, pipelineName, workspaceId, workspaceName])

  useEffect(() => {
    if (!isEdgeEnabled) {
      setNewAssayConfig(x => x.setLocations(null));
    }
    if (selectedEdges?.length) {
      setNewAssayConfig(x => x.setLocations(selectedEdges))
    } else {
      // Send all Edges + Ops Center if no filtered edges
      setNewAssayConfig(x => x.setLocations([
        { id: WALLAROO_OPS_CENTER_REGEX, name: WALLAROO_OPS_CENTER_REGEX },
        ...listOfEdgesForPipelines
      ]))
    }
  }, [isEdgeEnabled, listOfEdgesForPipelines, selectedEdges])

  const handleBack = () => {
    setError(null);
    setNextIsDisabled(false);
    // eslint-disable-next-line no-shadow
    setScreen((screen) => {
      switch (screen) {
      case CREATE_ASSAY_SCREENS.SELECT_MODEL:
        return CREATE_ASSAY_SCREENS.SELECT_MODEL;
      case CREATE_ASSAY_SCREENS.PREVIEW_HISTORICAL:
        return CREATE_ASSAY_SCREENS.SELECT_MODEL;
      default:
        return CREATE_ASSAY_SCREENS.SELECT_MODEL;
      }
    })
  }

  const handleNext = () => {
    // eslint-disable-next-line no-shadow
    setScreen((screen) => {
      switch (screen) {
      case CREATE_ASSAY_SCREENS.SELECT_MODEL:
        return CREATE_ASSAY_SCREENS.PREVIEW_HISTORICAL;
      case CREATE_ASSAY_SCREENS.PREVIEW_HISTORICAL:
        return CREATE_ASSAY_SCREENS.SELECT_MODEL;
      default:
        return CREATE_ASSAY_SCREENS.SELECT_MODEL;
      }
    })
  }

  const onBuild = async () => {
    try {
      if (areEdgesRequiredAndIncomplete(newAssayConfig)) {
        setError('Please provide all edge values if you wish to use a Custom Binning mode.')
      } else if (areWeightsRequiredAndIncomplete(newAssayConfig)) {
        setError('Please provide all weight values if you wish to use Custom Bin Weights.')
      } else if (areEdgeValuesInappropriate(newAssayConfig)) {
        setError('Please ensure your custom edge values are provided in sequential order.')
      } else {
        const ret = await createAssay(newAssayConfig, baselineSummary, selectedEdgeNames, isAssaysV2Enabled);
        // assay_id shouldn't be 0, so this is fine.
        if (ret?.assay_id || typeof ret === 'string') {
          matchMutate(/assay-list/);
          // The SDK waits 30 seconds for assay data to be calculated.
          setTimeout(() => {
            matchMutate(/assay-results/)
          }, 10000);
          onClose();
        } else if (ret.msg.includes('assay_name_key')) {
          setError('An assay already exists with this name.')
        } else {
          setError('Failed to create assay.');
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError('Failed to create assay.');
    }
  }

  const edgeLocationStr = `on ${numberOfSelectedEdges} filtered edges`;
  const showEdgeLocationString = isEdgeEnabled && isSelectModelScreen && hasSelectedEdgeLocations;

  const sortedLocations = sortEdgeLocations(selectedEdges);
  const tooltipLocations = sortedLocations.slice(0, 3).map(({ name }) => name);
  const remainingLocations = selectedEdges.length - tooltipLocations.length;
  const tooltipLabel = remainingLocations < 1 ? tooltipLocations.join('\n') : `${tooltipLocations.join('\n')},\n\u2026 & ${remainingLocations} x more`

  return (
    <CardView className="flex flex-col justify-evenly relative">
      {error && <Flash style={{
        position: 'absolute', top: 0, width: '80%', margin: '2px'
      }} variant="danger">{error}</Flash>}
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center">
          <CardHeader size="2xl">Create Assay</CardHeader>
          {
            showEdgeLocationString && (
              <Tooltip aria-label={tooltipLabel}>
                <Label className="ml-2" underline>
                  {edgeLocationStr}
                </Label>
              </Tooltip>
            )
          }
        </div>
        <Button
          variant="close"
          textColor="border-dark"
          hoverTextColor="border-darker"
          activeTextColor="info"
          onClick={onClose}
        />
      </div>
      <div className="overflow-scroll mt-2" style={{ height: '400px' }}>
        {
          screen === CREATE_ASSAY_SCREENS.SELECT_MODEL && <CreateAssayStepOne
            models={models}
            locations={selectedEdgeNames}
            selectedModel={newAssayConfig.window.model_name}
            setSelected={x => setNewAssayConfig(conf => new AssayConfig(conf.setModel(x)))}
            applyMonitor={(x) => {
              setError(null);
              return setNewAssayConfig(conf => new AssayConfig(conf.applyMonitor(x)))
            }}
            setName={x => setNewAssayConfig(conf => new AssayConfig(conf, { name: x }))}
            name={newAssayConfig.name}
            setNextIsDisabled={setNextIsDisabled}
            inferenceData={inferenceData}
            assayConfig={newAssayConfig}
            setError={setError}
            newAssayConfig={newAssayConfig}
            setNewAssayConfig={setNewAssayConfig}
          />
        }
        {
          screen === CREATE_ASSAY_SCREENS.PREVIEW_HISTORICAL && (
            <PreviewAndFinishWrapper
              newAssayConfig={newAssayConfig}
              setNewAssayConfig={setNewAssayConfig}
              setBaselineSummary={setBaselineSummary}
              setError={setError}
            />
          )
        }
      </div>
      <ToolTipButtonWrapper
        screen={screen}
        handleBack={handleBack}
        handleNext={handleNext}
        nextIsDisabled={nextIsDisabled}
        error={error}
        onBuild={onBuild}
        isEdgeEnabled={isEdgeEnabled}
      />
    </CardView>
  )
}

const ContextualAssayCreationModal = ({ onClose, inferenceData }) => {
  const { pipeline } = usePipelineData();
  const pipelinesAssocModelsQueryRes = useQuery(FETCH_PIPELINES_ASSOC_MODELS, {
    skip: !pipeline?.pipelineID,
    variables: { pipeline_name: pipeline?.pipelineID },
    pollInterval: 1000
  });
  const { currentWorkspaceId, currentWorkspace } = useWorkspaceContext();
  const { isEdgeEnabled } = useClientContext();

  return (
    <AssayCreationModalWrapper
      models={pipelinesAssocModelsQueryRes.data?.model}
      pipelineId={pipeline.id}
      pipelineName={pipeline.pipelineID}
      workspaceId={currentWorkspaceId}
      workspace={currentWorkspace}
      onClose={onClose}
      inferenceData={inferenceData}
      isEdgeEnabled={isEdgeEnabled}
    />
  )
}

export default ContextualAssayCreationModal;
